import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rfisApi, projectsApi, type Rfi } from '@/lib/apiClient';
import { StateView, Badge, Modal, Button, ActivityTimeline, SelectWithOther, FileAttachments } from '@/components';
import type { BadgeTone, StateViewQuery } from '@/components';
import { fmtDate } from '@/lib/dates';
import { dueStatus } from '@/lib/dueStatus';
import { useCanEditProject, useCanCreateRfi } from './OverviewEdit';
import RfiEmailModal from './RfiEmailModal';
import ContactOrRoleSelect, { type PartyValue } from './contacts/ContactOrRoleSelect';
import { useJobContacts, contactLine } from './contacts/jobContacts';
import { describeRecipients } from './contacts/RecipientsField';
import type { EmailRecipient } from '@/lib/apiClient';
import MarkSentModal from './MarkSentModal';
import { fmtSentAt, saveThenRefresh, toEasternInputValue } from './lib/sentAt';
import { saveBlob } from '../contracts/saveBlob';

// ─── Option lists (with an "Other…" escape hatch via SelectWithOther) ─────────
const ASSIGNEE_OPTIONS = ['Architect', 'Structural Eng.', 'MEP Eng.', 'Owner', 'GC', 'Subcontractor'];
const BALL_OPTIONS = ['Architect', 'Structural Eng.', 'MEP Eng.', 'Owner', 'E&W'];
// Priority is a fixed set (no "Other…") — a plain <select>.
const PRIORITY_OPTIONS = ['Low', 'Normal', 'High', 'Critical'];

// Project identity needed for the email subject/body + Job Folder uploads.
// Fetched once at the tab root and threaded down to the create modal + detail.
type RfiProject = { jobNumber: string; title: string; jobFolderUrl: string | null };

// Map a due-status tone to a text color for the list cell.
const DUE_COLOR: Record<BadgeTone, string | undefined> = {
  danger: 'var(--ew-danger)',
  warning: '#92400E',
  neutral: undefined,
  success: undefined,
  info: undefined,
};

// ─── Status → badge tone ────────────────────────────────────────────────────
// open → info, answered → success, closed → neutral. An open RFI whose due date
// has passed surfaces as a danger "Overdue" pill instead.
function statusBadge(rfi: Rfi): { label: string; tone: BadgeTone } {
  if (rfi.status === 'open') {
    const { daysUntil } = dueStatus(rfi.dueDate);
    if (daysUntil != null && daysUntil < 0) return { label: 'Overdue', tone: 'danger' };
    return { label: 'Open', tone: 'info' };
  }
  if (rfi.status === 'answered') return { label: 'Answered', tone: 'success' };
  if (rfi.status === 'closed') return { label: 'Closed', tone: 'neutral' };
  return { label: rfi.status, tone: 'neutral' };
}

// An open RFI past its due date.
function isOverdue(rfi: Rfi): boolean {
  if (rfi.status !== 'open') return false;
  const { daysUntil } = dueStatus(rfi.dueDate);
  return daysUntil != null && daysUntil < 0;
}

// ─── Workflow stepper ────────────────────────────────────────────────────────
const WORKFLOW_STEPS = ['Opened', 'Sent to assignee', 'Answered'];
// Map an RFI to its stepper index. answered/closed = final (2); open & notified =
// 1 (sent); open & not notified = 0 (just opened).
function stepIndex(rfi: Rfi): number {
  if (rfi.status === 'answered' || rfi.status === 'closed') return 2;
  return rfi.notifiedAt ? 1 : 0;
}

// ─── Filter chips ───────────────────────────────────────────────────────────
type FilterKey = 'all' | 'open' | 'answered' | 'overdue';
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'answered', label: 'Answered' },
  { key: 'overdue', label: 'Overdue' },
];

function matchesFilter(rfi: Rfi, filter: FilterKey): boolean {
  switch (filter) {
    case 'open': return rfi.status === 'open';
    case 'answered': return rfi.status === 'answered';
    case 'overdue': return isOverdue(rfi);
    default: return true;
  }
}

const css = `
  .rfi-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .rfi-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .rfi-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .rfi-table tr:last-child td { border-bottom: 0; }
  .rfi-row { cursor: pointer; transition: background var(--ew-dur-fast) var(--ew-ease); }
  .rfi-row:hover { background: var(--ew-n-50); }
  .rfi-num { font-variant-numeric: tabular-nums; font-weight: var(--ew-fw-medium); white-space: nowrap; }
  .rfi-muted { color: var(--ew-n-500); }
  /* SelectWithOther owns its own .swo-* styles now, so these no longer need to
     alias .sub-field/.sub-label/.sub-input on its behalf. */
  .rfi-field { display: flex; flex-direction: column; }
  .rfi-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 5px; }
  .rfi-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); width: 100%;
    background: #fff; color: var(--ew-dark);
  }
  .rfi-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--ew-s-3); }

  /* Filter chips */
  .rfi-chips { display: flex; gap: var(--ew-s-2); flex-wrap: wrap; }
  .rfi-chip {
    background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-pill);
    padding: 5px 12px; cursor: pointer; color: var(--ew-n-600);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    transition: all var(--ew-dur-fast) var(--ew-ease);
  }
  .rfi-chip:hover { border-color: var(--ew-n-300); color: var(--ew-dark); }
  .rfi-chip.active { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .rfi-chip-count {
    margin-left: 6px; font-variant-numeric: tabular-nums; font-size: 11px;
    color: var(--ew-n-400); font-weight: var(--ew-fw-semibold);
  }
  .rfi-chip.active .rfi-chip-count { color: rgba(255,255,255,0.7); }

  /* Workflow stepper */
  .rfi-step { display: flex; align-items: center; gap: var(--ew-s-3); padding: 9px 0; }
  .rfi-step-dot { width: 12px; height: 12px; border-radius: 50%; flex: 0 0 auto; }
  .rfi-step-label { flex: 1; font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font); }
  .rfi-step-tag {
    font: var(--ew-fw-semibold) 10px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase;
  }

  /* Detail */
  .rfi-back {
    background: transparent; border: 0; cursor: pointer; padding: 0;
    color: var(--ew-n-500); font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font);
    transition: color var(--ew-dur-fast) var(--ew-ease);
  }
  .rfi-back:hover { color: var(--ew-dark); }
  .rfi-section { padding: var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); }
  .rfi-section:last-child { border-bottom: 0; }
  .rfi-section-label { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); margin: 0 0 var(--ew-s-2); }
  .rfi-prose { font: var(--ew-fw-regular) var(--ew-fs-body) var(--ew-font); color: var(--ew-dark); line-height: 1.5; white-space: pre-wrap; }
  .rfi-detail-row { display: flex; justify-content: space-between; gap: var(--ew-s-4); padding: 9px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .rfi-detail-row:last-child { border-bottom: 0; }
  .rfi-detail-row span:first-child { color: var(--ew-n-500); }
  .rfi-detail-row span:last-child { color: var(--ew-dark); font-weight: var(--ew-fw-medium); text-align: right; }
  .rfi-help { font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .rfi-linkbtn {
    background: transparent; border: 0; cursor: pointer; padding: 0;
    color: var(--ew-quality); font: var(--ew-fw-medium) 12px/1.4 var(--ew-font);
    text-decoration: underline; text-underline-offset: 2px;
  }
  .rfi-mono { font-family: var(--ew-font-mono, ui-monospace, monospace); background: var(--ew-n-100); border-radius: var(--ew-r-sm); padding: 2px 6px; color: var(--ew-n-700); }
`;

// Suggest the next RFI number for the create form: take the highest trailing
// integer across the project's existing RFI numbers and increment, preserving
// the prefix + zero-padding it already uses ("RFI-007" → "RFI-008"). Defaults to
// "RFI-001" when there are none. This only PRE-FILLS — the field stays editable,
// so numbering stays consistent by default without being forced.
function nextRfiNumber(rfis: Rfi[]): string {
  let best: { n: number; prefix: string; width: number } | null = null;
  for (const r of rfis) {
    const m = /^(.*?)(\d+)\s*$/.exec(r.rfiNumber ?? '');
    if (!m) continue;
    const n = parseInt(m[2], 10);
    if (!best || n > best.n) best = { n, prefix: m[1], width: m[2].length };
  }
  if (!best) return 'RFI-001';
  return best.prefix + String(best.n + 1).padStart(best.width, '0');
}

const EMPTY_FORM = {
  rfiNumber: '',
  subject: '',
  specSection: '',
  priority: '',
  ballInCourt: '',
  dueDate: '',
  question: '',
};

export default function RfisTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const [params, setParams] = useSearchParams();
  const rfiParam = params.get('rfi');
  /**
   * `?rfi=new` — "open the create form", not "open RFI #new".
   *
   * The project header's "+ New RFI" button only called setTab('rfis'), which
   * dropped you on the list with nothing further to do; for a field leader the
   * create button wasn't even there, so the button appeared to do nothing at all
   * (product owner, 2026-08-04). It now asks for the form, and the request survives a
   * refresh because it lives in the URL.
   */
  const wantNew = rfiParam === 'new';
  const selectedId = wantNew ? null : rfiParam;

  const query = useQuery({
    queryKey: ['rfis', projectId],
    queryFn: () => rfisApi.list({ projectId }),
  });

  // Project identity for email subjects/bodies. Shares the workspace cache so
  // it's usually already warm; the composer falls back gracefully if not.
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string; title: string; jobFolderUrl: string | null }>,
    enabled: Number.isFinite(projectId),
    staleTime: 60_000,
  });
  const project: RfiProject = {
    jobNumber: projectQuery.data?.jobNumber ?? '',
    title: projectQuery.data?.title ?? '',
    jobFolderUrl: projectQuery.data?.jobFolderUrl ?? null,
  };

  // ?rfi=<id> → detail; keep ?tab=rfis intact when toggling.
  const openDetail = (id: number) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'rfis');
    next.set('rfi', String(id));
    setParams(next, { replace: true });
  };
  const backToList = () => {
    const next = new URLSearchParams(params);
    next.delete('rfi');
    setParams(next, { replace: true });
  };

  if (selectedId) {
    return (
      <>
        <style>{css}</style>
        <RfiDetail projectId={projectId} rfiId={Number(selectedId)} project={project} onBack={backToList} locked={locked} />
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <RfiList
        projectId={projectId}
        query={query}
        project={project}
        onOpen={openDetail}
        locked={locked}
        autoOpenCreate={wantNew}
        onCreateOpened={backToList}
      />
    </>
  );
}

// ─── List view (filter chips + create modal) ────────────────────────────────
function RfiList({
  projectId,
  query,
  project,
  onOpen,
  locked = false,
  autoOpenCreate = false,
  onCreateOpened,
}: {
  projectId: number;
  query: StateViewQuery<Rfi[]>;
  project: RfiProject;
  onOpen: (id: number) => void;
  locked?: boolean;
  /** `?rfi=new` arrived — open the create form once. */
  autoOpenCreate?: boolean;
  /** Clear the parameter, so a close-then-refresh doesn't reopen the form. */
  onCreateOpened?: () => void;
}) {
  const canEdit = useCanEditProject();
  const canCreate = useCanCreateRfi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<typeof EMPTY_FORM>(EMPTY_FORM);
  const [filter, setFilter] = useState<FilterKey>('all');
  // After "Send with Outlook" creates the RFI, hold it so the composer can open
  // prefilled from what the PM typed — and then so they can confirm the send.
  const [composeFor, setComposeFor] = useState<Rfi | null>(null);
  // The RFI to confirm, and who its draft was addressed to (#598 step 2).
  const [confirmFor, setConfirmFor] = useState<{ rfi: Rfi; recipients: EmailRecipient[] } | null>(null);
  // "Assigned to": a job contact, or a role label as before (#598 step 2).
  const NO_ASSIGNEE: PartyValue = { contactId: null, label: '' };
  const [assignee, setAssignee] = useState<PartyValue>(NO_ASSIGNEE);
  const { options: jobContacts, isLoading: contactsLoading } = useJobContacts(projectId, open);

  const set = (k: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));
  // For SelectWithOther, which yields a raw string value.
  const setVal = (k: keyof typeof EMPTY_FORM) => (v: string) =>
    setForm((p) => ({ ...p, [k]: v }));

  const openModal = () => {
    // Pre-fill the next sequential RFI # (editable) so numbering stays consistent.
    setForm({ ...EMPTY_FORM, rfiNumber: nextRfiNumber(query.data ?? []) });
    setAssignee(NO_ASSIGNEE);
    setOpen(true);
  };
  const closeModal = () => {
    setOpen(false);
    setForm(EMPTY_FORM);
    setAssignee(NO_ASSIGNEE);
  };

  /**
   * Honour `?rfi=new` once the RFI list has loaded.
   *
   * ⚠️ Waits for `query.data`, because `openModal` pre-fills the next sequential
   * RFI number from the existing list — firing on mount would hand the first
   * field leader to use this an empty number, and the numbering is the one thing
   * on this form people don't want to think about.
   *
   * Locked jobs (SYSTAC / ClosedWon) and users who can't create are ignored, so
   * a hand-typed URL can't conjure a form the buttons wouldn't offer.
   */
  useEffect(() => {
    if (!autoOpenCreate || open || locked || !canCreate || !query.data) return;
    openModal();
    onCreateOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCreate, locked, canCreate, query.data]);

  // Create an open RFI from the form (shared by both footer actions).
  const createOpen = () => {
    const ne = (v: string) => (v.trim() === '' ? undefined : v.trim());
    return rfisApi.create({
      projectId,
      rfiNumber: form.rfiNumber.trim(),
      subject: form.subject.trim(),
      specSection: ne(form.specSection),
      assignee: ne(assignee.label),
      ...(assignee.contactId != null ? { assigneeContactId: assignee.contactId } : {}),
      priority: ne(form.priority),
      ballInCourt: ne(form.ballInCourt),
      dueDate: ne(form.dueDate),
      question: ne(form.question),
    });
  };

  // "Create RFI" — create only, no email.
  const createMutation = useMutation({
    mutationFn: createOpen,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfis', projectId] });
      closeModal();
    },
  });

  // "Send with Outlook" — create (open), then open the composer in 'send' mode
  // for the created RFI. ⚠️ Nothing is marked sent here (#608): it used to call
  // sendToAssignee before the draft even opened, so the RFI read as sent while
  // the email sat in Drafts. The send is confirmed after the composer closes.
  const sendMutation = useMutation({
    mutationFn: createOpen,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['rfis', projectId] });
      setComposeFor(created);
      closeModal();
    },
  });

  const canSubmit = form.rfiNumber.trim() !== '' && form.subject.trim() !== '';
  const busy = createMutation.isPending || sendMutation.isPending;

  // Per-filter counts off the loaded list (so chips show a count pill).
  const all = query.data ?? [];
  const countFor = (key: FilterKey) => all.filter((r) => matchesFilter(r, key)).length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--ew-s-3)', marginBottom: 'var(--ew-s-4)', flexWrap: 'wrap' }}>
        <div className="rfi-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`rfi-chip${f.key === filter ? ' active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="rfi-chip-count">({countFor(f.key)})</span>
            </button>
          ))}
        </div>
        {/* ⚠️ canCREATE, not canEDIT — a field leader may raise an RFI on their
            own job without being able to answer, send or edit one. Every other
            control on this tab stays on canEdit. */}
        {locked || !canCreate ? null : <button className="btn btn-primary" onClick={openModal}>+ New RFI</button>}
      </div>

      <div className="pw-card">
        <h2 className="pw-card-hd">RFIs</h2>
        <StateView query={query} empty="No RFIs yet.">
          {(rfis) => {
            const filtered = rfis.filter((r) => matchesFilter(r, filter));
            if (filtered.length === 0) {
              return <p style={{ padding: 'var(--ew-s-4)', color: 'var(--ew-n-400)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', textAlign: 'center' }}>No RFIs match this filter.</p>;
            }
            return (
              <div className="ew-table-scroll">
                <table className="rfi-table ew-cards">
                  <thead>
                    <tr>
                      <th>RFI #</th>
                      <th>Subject</th>
                      <th>Spec</th>
                      <th>Ball in court</th>
                      <th>Status</th>
                      <th>Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((rfi) => {
                      const b = statusBadge(rfi);
                      // Color the due date only for open RFIs (overdue / due-soon).
                      const dueColor = rfi.status === 'open' ? DUE_COLOR[dueStatus(rfi.dueDate).tone] : undefined;
                      return (
                        <tr key={rfi.id} className="rfi-row" onClick={() => onOpen(rfi.id)}>
                          <td className="rfi-num" data-label="RFI #">{rfi.rfiNumber}</td>
                          <td data-label="Subject">{rfi.subject}</td>
                          <td className="rfi-muted" data-label="Spec">{rfi.specSection ?? '—'}</td>
                          <td className="rfi-muted" data-label="Ball in court">{rfi.ballInCourt ?? '—'}</td>
                          <td data-label="Status"><Badge label={b.label} tone={b.tone} /></td>
                          <td
                            className={dueColor ? undefined : 'rfi-muted'}
                            data-label="Due"
                            style={{ whiteSpace: 'nowrap', color: dueColor, fontWeight: dueColor ? 'var(--ew-fw-medium)' : undefined }}
                          >
                            {fmtDate(rfi.dueDate)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          }}
        </StateView>
      </div>

      <Modal
        isOpen={open}
        onClose={closeModal}
        title="New RFI"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={busy}>Cancel</Button>
            <Button
              variant="secondary"
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit || busy}
              isLoading={createMutation.isPending}
            >
              Create RFI
            </Button>
            {/* ⚠️ canEDIT, not canCreate. This one creates the RFI and then asks
                to mark it sent, which calls `/rfis/:id/send` on `projects.edit` —
                offering it to a field leader would end in a 403 after the email
                had gone. They submit; the PM sends. */}
            {canEdit && (
              <Button
                onClick={() => sendMutation.mutate()}
                disabled={!canSubmit || busy}
                isLoading={sendMutation.isPending}
              >
                Send with Outlook
              </Button>
            )}
          </>
        }
      >
        {(createMutation.isError || sendMutation.isError) && (
          <div style={{ marginBottom: 'var(--ew-s-3)', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
            {((createMutation.error ?? sendMutation.error) as Error).message}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-3)' }}>
          <div className="rfi-grid2">
            <div className="rfi-field">
              <label className="rfi-label">RFI #</label>
              <input className="rfi-input" value={form.rfiNumber} onChange={set('rfiNumber')} placeholder="e.g. RFI-001" />
              <p className="rfi-help" style={{ marginTop: 'var(--ew-s-2)' }}>Auto-numbered — edit if needed.</p>
            </div>
            <div className="rfi-field">
              <label className="rfi-label">Spec section</label>
              <input className="rfi-input" value={form.specSection} onChange={set('specSection')} placeholder="e.g. 03 30 00" />
            </div>
          </div>
          <div className="rfi-field">
            <label className="rfi-label">Subject</label>
            <input className="rfi-input" value={form.subject} onChange={set('subject')} />
          </div>
          <div className="rfi-grid2">
            <ContactOrRoleSelect
              label="Assigned to"
              contacts={jobContacts}
              loading={contactsLoading}
              roles={ASSIGNEE_OPTIONS}
              value={assignee}
              onChange={setAssignee}
              placeholder="Select assignee…"
            />
            <div className="rfi-field">
              <label className="rfi-label">Priority</label>
              <select className="rfi-input" value={form.priority} onChange={set('priority')}>
                <option value="">Select priority…</option>
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="rfi-grid2">
            <SelectWithOther
              label="Ball in court"
              options={BALL_OPTIONS}
              value={form.ballInCourt}
              onChange={setVal('ballInCourt')}
              placeholder="Select…"
            />
            <div className="rfi-field">
              <label className="rfi-label">Due date</label>
              <input className="rfi-input" type="date" value={form.dueDate} onChange={set('dueDate')} />
            </div>
          </div>
          <div className="rfi-field">
            <label className="rfi-label">Question</label>
            <textarea
              className="rfi-input"
              style={{ minHeight: 96, resize: 'vertical' }}
              value={form.question}
              onChange={set('question')}
            />
          </div>

          {/* ⚠️ Split on canEDIT, same reason as the Send button above: this modal
              is open to field leaders on canCreate, but FileAttachments hides its
              upload control on canEdit (and the attachment write path needs
              projects.edit anyway). Pointing them at the Job Folder card would be
              pointing at something they cannot use. They submit; the PM attaches. */}
          {!canEdit ? (
            <p className="rfi-help" style={{ marginTop: 'var(--ew-s-1)' }}>
              Submit the RFI here — your project manager adds any markups and sends
              it to the reviewer. Describe anything you would have attached in the
              question above.
            </p>
          ) : project.jobFolderUrl ? (
            <p className="rfi-help" style={{ marginTop: 'var(--ew-s-1)' }}>
              Markups and attachments are added after saving — open the RFI from the
              list and use its Job Folder card. That keeps a record of the documents
              on the RFI; emails still need the files attached in Outlook.
            </p>
          ) : (
            <p className="rfi-help" style={{ marginTop: 'var(--ew-s-1)' }}>
              This project has no Job Folder linked, so documents can't be kept on
              the RFI record yet — link one under Projects → edit. Emails need the
              files attached in Outlook either way.
            </p>
          )}
        </div>
      </Modal>

      {/* After "Send with Outlook" created the RFI, draft the email; once the
          draft has been used, ask whether it went. */}
      {composeFor && (
        <RfiEmailModal
          rfi={composeFor}
          project={project}
          projectId={projectId}
          mode="send"
          onClose={(used, recipients) => {
            if (used) setConfirmFor({ rfi: composeFor, recipients });
            setComposeFor(null);
          }}
        />
      )}
      {confirmFor && (
        <ConfirmRfiSent
          rfi={confirmFor.rfi}
          recipients={confirmFor.recipients}
          onSent={() => qc.invalidateQueries({ queryKey: ['rfis', projectId] })}
          onClose={() => setConfirmFor(null)}
        />
      )}
    </>
  );
}

/**
 * "Did you send it?" for an RFI (#608). Not sent yet records nothing, and Send
 * with Outlook stays on the RFI.
 */
function ConfirmRfiSent({ rfi, recipients = [], onSent, onClose }: {
  rfi: Rfi;
  /** Who the draft was addressed to, recorded with the send (#598 step 2). None when marked without a draft. */
  recipients?: EmailRecipient[];
  onSent: () => unknown;
  onClose: () => void;
}) {
  return (
    <MarkSentModal
      title="Did you send it?"
      intro={
        <>
          Mark <strong>{rfi.rfiNumber}</strong> as sent once the email has gone from Outlook. If it
          hasn&apos;t gone yet, choose <strong>Not sent yet</strong> — you can send it from the RFI later.
          {recipients.length > 0 && <> It will be recorded as sent to {describeRecipients(recipients)}.</>}
        </>
      }
      min={toEasternInputValue(rfi.createdAt)}
      // An answered RFI cannot have been sent after its answer; the API refuses that.
      max={rfi.respondedAt ? toEasternInputValue(rfi.respondedAt) : undefined}
      confirmLabel="Mark as sent"
      cancelLabel="Not sent yet"
      onSubmit={(sentAt) => saveThenRefresh(rfisApi.sendToAssignee(rfi.id, sentAt, recipients), onSent)}
      onClose={onClose}
    />
  );
}

// ─── Detail view ─────────────────────────────────────────────────────────────
function RfiDetail({
  projectId,
  rfiId,
  project,
  onBack,
  locked = false,
}: {
  projectId: number;
  rfiId: number;
  project: RfiProject;
  onBack: () => void;
  locked?: boolean;
}) {
  // Activity-locked (SYSTAC / ClosedWon, no Sage number yet) is read-only regardless
  // of permission, as on the Equipment, Materials, Changes and Receiving tabs. One
  // flag, so it covers every control below that is gated on it: the response, ball
  // in court, send and reminder, reopen, and the Job Folder upload and remove.
  //
  // ⚠️ UI only. The server gates CREATING an RFI, not changing one, and cannot
  // simply be told to: ProjectStageGuard reads `:id` as a PROJECT id first, which
  // on /rfis/:id is an RFI id, so it would check the wrong project.
  const canEdit = useCanEditProject() && !locked;
  const qc = useQueryClient();
  const [responseText, setResponseText] = useState('');
  // Which composer (if any) is open, and for which RFI snapshot.
  const [compose, setCompose] = useState<{ mode: 'send' | 'reminder'; rfi: Rfi } | null>(null);
  // Who the last send draft was addressed to, for the confirm step (#598 step 2).
  const [draftedTo, setDraftedTo] = useState<EmailRecipient[]>([]);
  // Changing who it's assigned to: the value being picked, while the dialog is open.
  const [assigning, setAssigning] = useState<PartyValue | null>(null);
  const [assignPending, setAssignPending] = useState(false);
  const { options: jobContacts, isLoading: contactsLoading } = useJobContacts(projectId, canEdit);
  // The sent-time dialog: confirming a send, or correcting one (#608).
  const [sentDialog, setSentDialog] = useState<'confirm' | 'correct' | null>(null);
  // The sent time the correction dialog was OPENED with. ⚠️ Held in state, not read
  // from the query: a background refetch would otherwise quietly move it, and the
  // server's "has this changed since you opened it?" check would compare against a
  // time this user never saw.
  const [openedWith, setOpenedWith] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['rfi', rfiId],
    queryFn: () => rfisApi.get(rfiId),
  });

  // Activity timeline (who-did-what-when). Short staleTime so it stays fresh
  // without refetching on every render; explicitly invalidated below after any
  // workflow action so a new event shows immediately.
  const activityQuery = useQuery({
    queryKey: ['rfi-activity', rfiId],
    queryFn: () => rfisApi.activity(rfiId),
    staleTime: 30_000,
  });

  // Returns the refetch, so a dialog that awaits it closes on fresh data rather
  // than flashing the buttons the save just made stale.
  const invalidate = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['rfis', projectId] }),
    qc.invalidateQueries({ queryKey: ['rfi', rfiId] }),
    qc.invalidateQueries({ queryKey: ['rfi-activity', rfiId] }),
  ]);

  const respondMutation = useMutation({
    mutationFn: () => rfisApi.respond(rfiId, { response: responseText.trim() }),
    onSuccess: () => {
      setResponseText('');
      invalidate();
    },
  });

  // Reassign ball-in-court from the sidebar SelectWithOther (fires on change).
  const reassignMutation = useMutation({
    mutationFn: (ballInCourt: string) => rfisApi.update(rfiId, { ballInCourt }),
    onSuccess: invalidate,
  });

  const reopenMutation = useMutation({
    mutationFn: () => rfisApi.reopen(rfiId),
    onSuccess: invalidate,
  });

  // Who it's assigned to: a job contact, or a role label (#598 step 2).
  const assignMutation = useMutation({
    mutationFn: (v: PartyValue) => rfisApi.update(rfiId, { assignee: v.label.trim(), assigneeContactId: v.contactId }),
    onSuccess: async () => { await invalidate(); setAssigning(null); },
  });

  // The RFI on the company's Excel form (#645). The API names the file the house way;
  // the fallback is only for a browser that was not allowed to read that header.
  const exportMutation = useMutation({
    mutationFn: () => rfisApi.exportForm(rfiId),
    onSuccess: ({ blob, fileName }) => saveBlob(blob, fileName ?? `RFI ${query.data?.rfiNumber ?? rfiId}.xlsx`),
  });

  return (
    <>
      <button className="rfi-back" onClick={onBack} style={{ marginBottom: 'var(--ew-s-4)' }}>← Back to RFIs</button>

      <StateView query={query} isEmpty={(d) => !d} empty="RFI not found.">
        {(rfi) => {
          const b = statusBadge(rfi);
          const overdue = isOverdue(rfi);
          const awaitingFrom = rfi.ballInCourt?.trim() || 'assignee';
          const canSubmit = responseText.trim() !== '';
          const stateIdx = stepIndex(rfi);
          const due = dueStatus(rfi.dueDate);
          const dueColor = DUE_COLOR[due.tone];

          return (
            <div className="ew-two-col">
              {/* Left column — content + workflow stepper + response */}
              <div style={{ display: 'grid', gap: 'var(--ew-s-4)' }}>
                <div className="pw-card">
                  <div className="rfi-section">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', flexWrap: 'wrap' }}>
                      <span className="rfi-num" style={{ font: 'var(--ew-fw-semibold) 18px/1 var(--ew-font)' }}>{rfi.rfiNumber}</span>
                      <Badge label={b.label} tone={b.tone} />
                      {overdue && b.label !== 'Overdue' ? <Badge label="Overdue" tone="danger" /> : null}
                    </div>
                    <h1 style={{ font: 'var(--ew-fw-semibold) 22px/1.2 var(--ew-font)', letterSpacing: '-0.01em', margin: '10px 0 0' }}>{rfi.subject}</h1>
                  </div>

                  <div className="rfi-section">
                    <p className="rfi-section-label">Question</p>
                    {rfi.question
                      ? <p className="rfi-prose">{rfi.question}</p>
                      : <p className="rfi-prose rfi-muted" style={{ color: 'var(--ew-n-400)' }}>No question recorded.</p>}
                  </div>

                  <div className="rfi-section">
                    <p className="rfi-section-label">Response</p>
                    {rfi.response
                      ? <p className="rfi-prose">{rfi.response}</p>
                      : <p className="rfi-prose" style={{ color: 'var(--ew-n-400)' }}>Awaiting response from {awaitingFrom}…</p>}
                  </div>
                </div>

                {/* Workflow stepper */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Workflow</h2>
                  <div className="rfi-section" style={{ borderBottom: 0 }}>
                    {WORKFLOW_STEPS.map((label, i) => {
                      // ⚠️ An answered RFI is past "Sent to assignee" whether or not it
                      // was ever sent — it can be answered without being marked sent,
                      // or marked not sent afterwards (#608). Only call that step done
                      // when there is a send on record; otherwise it reads as skipped.
                      const skipped = i === 1 && i < stateIdx && !rfi.notifiedAt;
                      const done = i < stateIdx && !skipped;
                      const current = i === stateIdx;
                      // done → green, current → quality (blue), future → neutral.
                      const dotColor = done ? 'var(--ew-green)' : current ? 'var(--ew-quality)' : 'var(--ew-n-200)';
                      const tagColor = done ? 'var(--ew-green)' : current ? 'var(--ew-quality)' : 'var(--ew-n-400)';
                      return (
                        <div key={label} className="rfi-step">
                          <span className="rfi-step-dot" style={{ background: dotColor }} />
                          <span className="rfi-step-label" style={{ color: i > stateIdx || skipped ? 'var(--ew-n-400)' : 'var(--ew-dark)' }}>{label}</span>
                          {(done || current) && (
                            <span className="rfi-step-tag" style={{ color: tagColor }}>{done ? 'done' : 'current'}</span>
                          )}
                          {skipped && (
                            <span className="rfi-step-tag" style={{ color: 'var(--ew-n-400)' }}>not recorded</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Log a response */}
                {canEdit && (
                <div className="pw-card">
                  <h2 className="pw-card-hd">Log a response</h2>
                  <div className="rfi-section" style={{ borderBottom: 0 }}>
                    {respondMutation.isError && (
                      <div style={{ marginBottom: 'var(--ew-s-3)', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
                        {(respondMutation.error as Error).message}
                      </div>
                    )}
                    <textarea
                      className="rfi-input"
                      style={{ minHeight: 110, resize: 'vertical' }}
                      placeholder="Type the response to this RFI…"
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--ew-s-3)' }}>
                      <Button
                        variant="primary"
                        onClick={() => respondMutation.mutate()}
                        disabled={!canSubmit}
                        isLoading={respondMutation.isPending}
                      >
                        Submit Response &amp; Close
                      </Button>
                    </div>
                  </div>
                </div>
                )}

                {/* History — generic activity timeline (shared with submittals) */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">History</h2>
                  <div className="rfi-section" style={{ borderBottom: 0 }}>
                    <ActivityTimeline events={activityQuery.data ?? []} />
                  </div>
                </div>
              </div>

              {/* Right column — metadata + assignee notification + SharePoint stub */}
              <div style={{ display: 'grid', gap: 'var(--ew-s-4)' }}>
                <div className="pw-card">
                  <h2 className="pw-card-hd">Details</h2>
                  <div className="rfi-detail-row"><span>Status</span><span><Badge label={b.label} tone={b.tone} /></span></div>
                  <div className="rfi-detail-row"><span>Spec section</span><span>{rfi.specSection ?? '—'}</span></div>
                  <div className="rfi-detail-row" style={{ alignItems: 'flex-start' }}>
                    <span>Assigned to</span>
                    <span style={{ textAlign: 'right' }}>
                      {rfi.assigneeContact ? contactLine(rfi.assigneeContact) : (rfi.assignee ?? '—')}
                      {rfi.assigneeContact?.email && (
                        <><br /><a href={`mailto:${rfi.assigneeContact.email}`}>{rfi.assigneeContact.email}</a></>
                      )}
                      {canEdit && (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="rfi-linkbtn"
                            onClick={() => setAssigning({
                              contactId: rfi.assigneeContact?.id ?? null,
                              label: rfi.assigneeContact?.name ?? rfi.assignee ?? '',
                            })}
                          >
                            Change
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                  <div className="rfi-detail-row"><span>Priority</span><span>{rfi.priority ?? '—'}</span></div>
                  <div className="rfi-detail-row"><span>Opened</span><span>{fmtDate(rfi.createdAt)}</span></div>
                  <div className="rfi-detail-row">
                    <span>Due</span>
                    <span style={{ color: dueColor, fontWeight: dueColor ? 'var(--ew-fw-semibold)' : undefined }}>{fmtDate(rfi.dueDate)}</span>
                  </div>
                  <div className="rfi-detail-row" style={{ alignItems: 'center' }}>
                    <span>Ball in court</span>
                    <span style={{ minWidth: 150 }}>
                      {canEdit ? (
                        <SelectWithOther
                          options={BALL_OPTIONS}
                          value={rfi.ballInCourt ?? ''}
                          onChange={(v) => { if (v && v !== rfi.ballInCourt) reassignMutation.mutate(v); }}
                          placeholder="Unassigned"
                        />
                      ) : (
                        <span>{rfi.ballInCourt ?? 'Unassigned'}</span>
                      )}
                    </span>
                  </div>
                  {reassignMutation.isError && (
                    <p className="rfi-help" style={{ color: 'var(--ew-danger)', padding: '0 var(--ew-s-4) var(--ew-s-2)' }}>
                      {(reassignMutation.error as Error).message}
                    </p>
                  )}
                </div>

                {/* Assignee notification — manual Outlook model (no auto-reminders) */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Assignee notification</h2>
                  <div className="rfi-section" style={{ borderBottom: 0 }}>
                    {/* The sent time is shown in Eastern with the zone named: it is
                        what was typed when the send was confirmed, and a date alone
                        read a 9 PM send as the next day (fmtDate on a UTC instant). */}
                    <p className="rfi-help" style={{ marginBottom: 'var(--ew-s-3)' }}>
                      {rfi.notifiedAt
                        ? <span style={{ color: 'var(--ew-dark)' }}>✉ Assignee notified {fmtSentAt(rfi.notifiedAt)}</span>
                        : 'Not yet sent.'}
                      {rfi.notifiedAt && (rfi.notifiedTo?.length ?? 0) > 0 && (
                        <span style={{ display: 'block', marginTop: 'var(--ew-s-1)' }}>
                          Sent to {describeRecipients(rfi.notifiedTo!)}
                        </span>
                      )}
                      {canEdit && rfi.notifiedAt && (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="rfi-linkbtn"
                            aria-label="Edit the sent time"
                            onClick={() => { setOpenedWith(rfi.notifiedAt); setSentDialog('correct'); }}
                          >
                            Edit
                          </button>
                        </>
                      )}
                    </p>

                    {/* Status-aware action: un-notified → Mark as sent (for an email
                        that went without the composer, or after "Not sent yet"), plus
                        Send while open; open & notified → Send a reminder.
                        ⚠️ Mark as sent is offered on an answered or closed RFI too:
                        Edit → Mark as not sent works at any status, and without this
                        a cleared answered RFI had no way back short of Reopen. */}
                    {canEdit && !rfi.notifiedAt ? (
                      <div style={{ display: 'flex', gap: 'var(--ew-s-2)', flexWrap: 'wrap' }}>
                        {rfi.status === 'open' && (
                          <Button variant="primary" onClick={() => setCompose({ mode: 'send', rfi })}>
                            Send with Outlook
                          </Button>
                        )}
                        <Button variant="secondary" onClick={() => { setDraftedTo([]); setSentDialog('confirm'); }}>
                          Mark as sent…
                        </Button>
                      </div>
                    ) : canEdit && rfi.status === 'open' && rfi.notifiedAt ? (
                      <Button variant="secondary" onClick={() => setCompose({ mode: 'reminder', rfi })} disabled={contactsLoading}>
                        Send a reminder
                      </Button>
                    ) : null}

                    {/* The company RFI form, filled in (#645). A read, so it is offered to
                        everyone who can open the RFI, at any status and on a locked job. */}
                    <div style={{ marginTop: 'var(--ew-s-3)' }}>
                      <Button
                        variant="secondary"
                        onClick={() => exportMutation.mutate()}
                        isLoading={exportMutation.isPending}
                      >
                        Export to Excel
                      </Button>
                      <p className="rfi-help" style={{ marginTop: 'var(--ew-s-2)' }}>
                        The company RFI form, filled in from this RFI. Boxes left yellow are for you to fill in by hand.
                      </p>
                      {exportMutation.isError && (
                        <p className="rfi-help" style={{ color: 'var(--ew-danger)', marginTop: 'var(--ew-s-2)' }}>
                          {(exportMutation.error as Error).message}
                        </p>
                      )}
                    </div>

                    {rfi.status === 'open' && (
                      <div style={{ marginTop: 'var(--ew-s-3)' }}>
                        <Badge label={due.label} tone={due.tone} />
                      </div>
                    )}

                    {canEdit && rfi.status === 'open' && (
                      <p className="rfi-help" style={{ marginTop: 'var(--ew-s-3)', fontStyle: 'italic' }}>
                        Opens an Outlook draft — you send it, then mark it sent here.
                      </p>
                    )}
                  </div>
                </div>

                {/* Reopen (answered/closed only) */}
                {canEdit && rfi.status !== 'open' ? (
                  <div className="pw-card">
                    <h2 className="pw-card-hd">Actions</h2>
                    <div className="rfi-section" style={{ borderBottom: 0 }}>
                      <Button
                        variant="secondary"
                        onClick={() => reopenMutation.mutate()}
                        isLoading={reopenMutation.isPending}
                      >
                        Reopen RFI
                      </Button>
                      {reopenMutation.isError && (
                        <p className="rfi-help" style={{ color: 'var(--ew-danger)', marginTop: 'var(--ew-s-2)' }}>
                          {(reopenMutation.error as Error).message}
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}

                {/* SharePoint Job Folder — delegated-Graph upload (link + metadata) */}
                <FileAttachments
                  canEdit={canEdit}
                  entityType="rfi"
                  entityId={rfi.id}
                  jobFolderUrl={project.jobFolderUrl}
                  subfolder="RFIs"
                  jobNumber={project.jobNumber}
                  docNumber={rfi.rfiNumber}
                  shortDescSource={rfi.subject}
                  status={rfi.status}
                />
              </div>
            </div>
          );
        }}
      </StateView>

      {/* Outlook draft composer — 'send' or 'reminder'. Opening it records
          nothing; a used 'send' draft is followed by "Did you send it?". */}
      {compose && (
        <RfiEmailModal
          rfi={compose.rfi}
          project={project}
          projectId={projectId}
          mode={compose.mode}
          previousRecipients={compose.mode === 'reminder' ? compose.rfi.notifiedTo : undefined}
          onClose={(used, recipients) => {
            if (compose.mode === 'send' && used) { setDraftedTo(recipients); setSentDialog('confirm'); }
            setCompose(null);
          }}
        />
      )}

      {sentDialog === 'confirm' && query.data && (
        <ConfirmRfiSent rfi={query.data} recipients={draftedTo} onSent={invalidate} onClose={() => setSentDialog(null)} />
      )}
      {/* ⚠️ Gated on the RFI, not on its notifiedAt. A refused save refetches, and
          if someone else cleared the send meanwhile, a notifiedAt gate would make
          the dialog vanish with its error unread — and reappear on its own the
          next time the RFI was marked sent. The field keeps the value it opened
          with; only closing the dialog clears sentDialog. */}
      {/* Who it's assigned to (#598 step 2). A dialog with Save, not a pick that
          saves on change, so typing under "Other…" doesn't save every keystroke. */}
      {assigning && (
        <Modal
          isOpen
          onClose={() => { if (!assignMutation.isPending) { setAssigning(null); assignMutation.reset(); } }}
          title="Assigned to"
          maxWidth={480}
          footer={
            <>
              <Button variant="secondary" onClick={() => { setAssigning(null); assignMutation.reset(); }} disabled={assignMutation.isPending}>Cancel</Button>
              <Button
                onClick={() => assignMutation.mutate(assigning)}
                isLoading={assignMutation.isPending}
                disabled={assignPending || (assigning.contactId == null && !assigning.label.trim())}
              >
                Save
              </Button>
            </>
          }
        >
          <ContactOrRoleSelect
            contacts={jobContacts}
            loading={contactsLoading}
            roles={ASSIGNEE_OPTIONS}
            value={assigning}
            onChange={setAssigning}
            current={query.data?.assigneeContact}
            onPendingChange={setAssignPending}
            placeholder="Select assignee…"
          />
          {assignMutation.isError && (
            <p className="rfi-help" role="alert" style={{ color: 'var(--ew-danger)', marginTop: 'var(--ew-s-2)' }}>
              {(assignMutation.error as Error).message}
            </p>
          )}
        </Modal>
      )}

      {sentDialog === 'correct' && query.data && (
        <MarkSentModal
          title="Correct the sent time"
          intro={
            <>
              When did <strong>{query.data.rfiNumber}</strong> actually go to the assignee? If it was
              never sent, mark it as not sent. The change is kept in the RFI&apos;s history.
            </>
          }
          initialValue={openedWith ? toEasternInputValue(openedWith) : undefined}
          min={toEasternInputValue(query.data.createdAt)}
          max={query.data.respondedAt ? toEasternInputValue(query.data.respondedAt) : undefined}
          confirmLabel="Save"
          cancelLabel="Cancel"
          onSubmit={(sentAt) => saveThenRefresh(rfisApi.correctSentAt(rfiId, sentAt, openedWith), invalidate)}
          onClear={() => saveThenRefresh(rfisApi.correctSentAt(rfiId, null, openedWith), invalidate)}
          onClose={() => setSentDialog(null)}
        />
      )}
    </>
  );
}
